import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface HeaderProps {
  darkMode?: boolean;
}

export default function Header({ darkMode = false }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Projects', href: '#projects' },
    { label: 'Industries', href: '#industries' },
    { label: 'Why Choose Us', href: '#why-choose-us' },
    { label: 'Careers', href: '#careers' },
  ];

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const getTextColor = () => {
    if (isScrolled) return 'text-gray-800 hover:text-[#C9A961]';
    if (darkMode) return 'text-gray-800 hover:text-[#C9A961]';
    return 'text-white hover:text-[#C9A961]';
  };

  const getIconColor = () => {
    if (isScrolled) return 'text-gray-800';
    if (darkMode) return 'text-gray-800';
    return 'text-white';
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e87bcf04e7625c18d803186005e49f89/d3b85133aa0f2052ce6d4a05a6d7dbde.png"
              alt="Vertak Group"
              className="h-12 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${getTextColor()}`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/clients"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${getTextColor()}`}
            >
              Clients
            </Link>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="px-6 py-2.5 bg-[#C9A961] text-white text-sm font-medium rounded-lg hover:bg-[#B89851] transition-all whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </a>
          </nav>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl ${getIconColor()}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg">
          <nav className="px-6 py-4 space-y-3">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="block text-gray-800 hover:text-[#C9A961] text-sm font-medium transition-colors cursor-pointer"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/clients"
              className="block text-gray-800 hover:text-[#C9A961] text-sm font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Clients
            </Link>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="block px-6 py-2.5 bg-[#C9A961] text-white text-sm font-medium rounded-lg hover:bg-[#B89851] transition-all text-center cursor-pointer"
            >
              Contact Us
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}